import { Box, Grid, Alert, Tooltip, IconButton, Typography, Drawer, List, ListItemText, Divider } from '@mui/material'
import React, { useEffect, useState } from 'react'
import Header from './Header'
import styles from './index.module.css'
import ProductDetails from './Presentation'
import { Container } from '@mui/material'
import Total from './Total'
import Footer from './Footer'
import { useProjectTemplateConfig } from '../../hooks/useProjectTemplateConfig'
import {
  fetchPresentationDetails,
  postComment,
  getProductComments,
  editComment as svcEditComment,
  deleteComment as svcDeleteComment,
  listPresentationRevisions,
} from '../../services/presentation'
import { useParams, useLocation } from 'react-router-dom'
import LoadingComponent from '../ComponentLoader'
import EditIcon from '@mui/icons-material/Edit'
import HistoryIcon from '@mui/icons-material/History'
import CloseIcon from '@mui/icons-material/Close'
import DocumentSectionTitle from '../GeneratedSalesOrder/DocumentSectionTitle'
import { resolveCustomerDocumentLayoutRows } from '../customerDocumentLayout'

// decode base64url safely (for edit button gating)
const decodeBase64Url = (str) => {
  try {
    const pad = '='.repeat((4 - (str.length % 4)) % 4)
    const b64 = (str + pad).replace(/-/g, '+').replace(/_/g, '/')
    const json = atob(b64)
    const text = decodeURIComponent(
      json
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    )
    return JSON.parse(text)
  } catch {
    try {
      return JSON.parse(atob(str))
    } catch {
      return null
    }
  }
}

const getRoleFromAccessToken = () => {
  const token = localStorage.getItem('accessToken')
  if (!token) return null
  const parts = token.split('.')
  if (parts.length !== 3) return null
  const payload = decodeBase64Url(parts[1])
  return payload?.role ?? payload?.user?.role ?? null
}

const removedMaskSx = {
  opacity: 0.55,
  filter: 'grayscale(1)',
  pointerEvents: 'none',
  userSelect: 'none',
}
const removedBannerSx = {
  position: 'absolute',
  top: 10,
  right: 10,
  zIndex: 2,
  background: '#b71c1c',
  color: '#fff',
  px: 1.25,
  py: 0.75,
  borderRadius: 1,
  fontWeight: 700,
  letterSpacing: '0.3px',
  boxShadow: '0 2px 6px rgba(0,0,0,.15)',
}

const getInstanceSlug = (pathname) => {
  const m = pathname.match(/\/instance\/([^/]+)/i)
  return m?.[1] || 'elite-promo-marketing'
}

const Presentations = () => {
  const { id } = useParams()
  const location = useLocation()
  const instanceSlug = getInstanceSlug(location.pathname)
  const { headerConfig, footerAddress } = useProjectTemplateConfig({
    instanceUniqueId: instanceSlug,
    viewKey: 'presentation',
  })

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [presentationDetails, setPresentationalDetails] = useState(null)
  const [busyCommentAction, setBusyCommentAction] = useState(false)

  // history state (minimal)
  const [historyOpen, setHistoryOpen] = useState(false)
  const [historyList, setHistoryList] = useState([])
  const [loadingHistory, setLoadingHistory] = useState(false)
  const [historyError, setHistoryError] = useState(false)

  // fetch presentation
  const reload = async () => {
    const res = await fetchPresentationDetails(id)
    setPresentationalDetails(res?.presentation ?? null)
  }

  useEffect(() => {
    let alive = true
    const load = async () => {
      try {
        setLoading(true)
        setError(null)
        const res = await fetchPresentationDetails(id)
        if (!alive) return
        setPresentationalDetails(res?.presentation ?? null)
      } catch (e) {
        if (!alive) return
        setError(e?.message || 'Failed to load presentation')
        setPresentationalDetails(null)
      } finally {
        if (alive) setLoading(false)
      }
    }
    if (id) load()
    return () => {
      alive = false
    }
  }, [id])

  // history: load when drawer opens
  const loadHistory = async () => {
    if (!presentationDetails?.orderId) return
    try {
      setLoadingHistory(true)
      setHistoryError(false)
      const list = await listPresentationRevisions(presentationDetails.orderId, id)
      setHistoryList(Array.isArray(list) ? list : [])
    } catch (e) {
      console.error('Failed to load presentation revisions', e)
      setHistoryList([])
      setHistoryError(true)
    } finally {
      setLoadingHistory(false)
    }
  }

  useEffect(() => {
    if (historyOpen) loadHistory()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historyOpen, presentationDetails?.orderId, id])

  // ── comments wiring (kept as-is) ───────────────────────────────────────
  const handleAddComment = async (threadId, productId, comment) => {
    const presentationId = threadId || presentationDetails?.presentationId
    if (!presentationId || !productId || !comment?.trim()) return
    try {
      setBusyCommentAction(true)
      await postComment({ presentationId, productId, comment })
      await reload()
    } catch (e) {
      console.error('Failed to post comment', e)
      alert('Failed to post comment. Please try again.')
    } finally {
      setBusyCommentAction(false)
    }
  }
  const handleFetchComments = async (productId) => {
    const presentationId = presentationDetails?.presentationId
    if (!presentationId || !productId) return { comments: [] }
    try {
      const data = await getProductComments(presentationId, productId)
      return data
    } catch (e) {
      console.error('Failed to load comments', e)
      return { comments: [] }
    }
  }
  const handleEditComment = async (commentId, updatedComment) => {
    if (!commentId || !updatedComment?.trim()) return
    try {
      setBusyCommentAction(true)
      await svcEditComment(commentId, updatedComment)
      await reload()
    } catch (e) {
      console.error('Failed to edit comment', e)
      alert('Failed to edit comment. Please try again.')
    } finally {
      setBusyCommentAction(false)
    }
  }
  const handleDeleteComment = async (commentId) => {
    if (!commentId) return
    try {
      setBusyCommentAction(true)
      await svcDeleteComment(commentId)
      await reload()
    } catch (e) {
      console.error('Failed to delete comment', e)
      alert('Failed to delete comment. Please try again.')
    } finally {
      setBusyCommentAction(false)
    }
  }

  // ── toggles ───────────────────────────────────────────────────────────
  const showPricing = !Boolean(presentationDetails?.hidePrice)
  const hideProductTotals = presentationDetails?.settings?.hideProductTotals !== false
  const showArtwork = presentationDetails?.settings?.showArtwork !== false
  const canComment = presentationDetails?.settings?.allowComments !== false
  const showTotals = presentationDetails?.settings?.showTotals !== false
  const showHeaderTotals = !!presentationDetails && !loading && showTotals && showPricing
  const renderedLayoutRows = React.useMemo(() => {
    return resolveCustomerDocumentLayoutRows({
      products: Array.isArray(presentationDetails?.productDetails) ? presentationDetails.productDetails : [],
      cartSections: Array.isArray(presentationDetails?.cartSections) ? presentationDetails.cartSections : [],
      layout: Array.isArray(presentationDetails?.layout) ? presentationDetails.layout : [],
    })
  }, [presentationDetails?.cartSections, presentationDetails?.layout, presentationDetails?.productDetails])

  // ── edit FAB ───────────────────────────────────────────────────────────
  const hasAccessToken = Boolean(localStorage.getItem('accessToken'))
  const role = String(getRoleFromAccessToken() || localStorage.getItem('userRole') || '').toLowerCase()
  const canEdit = hasAccessToken && role !== 'customer'
  const buildEditUrl = () => {
    const orderId = presentationDetails?.orderId
    if (!orderId) return '#'
    return `/instance/${instanceSlug}/sales/orders/${orderId}/presentation`
  }

  return (
    <>
      <Box className={styles.rootContainer}>
        <Container className={styles.printDocument} maxWidth="lg">
          {/* Header always renders */}
          <Header
            presentationDate={presentationDetails?.presentationDate}
            id={presentationDetails?.presentationId}
            Introduction={presentationDetails?.introduction || 'review.'}
            headerConfig={headerConfig}
            isLoaded={!!presentationDetails && !loading}
          />

          {!loading && presentationDetails && !showPricing && (
            <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
              Pricing is intentionally hidden for this presentation. Quantities, artwork details, and specs are still
              shown.
            </Alert>
          )}

          {showHeaderTotals && (
            <Total
              currencyCode={presentationDetails?.currency}
              totalPrice={presentationDetails?.total}
              Introduction="Have comments or special instructions for any of the products listed? Use the sections below to share your thoughts - your input helps us prepare the most accurate estimate possible."
            />
          )}

          <Box className={styles.PresentationContainer}>
            <Grid container spacing={4} className={styles.presentationCard}>
              {loading || !presentationDetails ? (
                <Grid item xs={12} className={styles.loaderContainer}>
                  <LoadingComponent />
                  {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
                </Grid>
              ) : (
                renderedLayoutRows.map((row, index) => {
                  if (row.type === 'section') {
                    return (
                      <Grid item xs={12} key={row.key} className={`${styles.presentationGrid} ${styles.sectionTitleGrid}`}>
                        <DocumentSectionTitle title={row.title} style={row.style} />
                      </Grid>
                    )
                  }

                  const data = row.product
                  const isRemoved = Boolean(data?.__removed)
                  return (
                    <Grid
                      item
                      xs={12}
                      key={row.key || index}
                      className={styles.presentationGrid}
                      sx={{ position: 'relative' }}
                    >
                      {isRemoved && (
                        <Box sx={removedBannerSx}>This product was removed from the order</Box>
                      )}
                      <Box sx={isRemoved ? removedMaskSx : undefined}>
                        <ProductDetails
                          productData={data}
                          currency={presentationDetails?.currency}
                          estimationId={presentationDetails?.presentationId}
                          presentationId={presentationDetails?.presentationId}
                          presentationDetails={presentationDetails}
                          isRemoved={isRemoved}
                          showPricing={showPricing && !isRemoved}
                          showProductTotals={!hideProductTotals}
                          showArtwork={showArtwork && !isRemoved}
                          canComment={canComment && !busyCommentAction && !isRemoved}
                          handleComments={handleAddComment}
                          fetchComments={handleFetchComments}
                          handleEditComment={handleEditComment}
                          handleDeleteComment={handleDeleteComment}
                        />
                      </Box>
                    </Grid>
                  )
                })
              )}
            </Grid>
          </Box>

          <Footer rep={presentationDetails?.rep} footerAddress={footerAddress} />
        </Container>
      </Box>
      {/* Edit FAB */}
      {canEdit && presentationDetails && (
        <Box sx={{ position: 'fixed', bottom: 16, left: 16, zIndex: 2000 }}>
          <Tooltip title="Edit presentation">
            <IconButton
              size="large"
              aria-label="Edit presentation"
              onClick={() => window.open(buildEditUrl(), '_blank')}
              sx={{
                bgcolor: '#1976d2',
                color: '#fff',
                border: '1px solid #0f4fa1',
                boxShadow: 2,
                '&:hover': { boxShadow: 4, bgcolor: '#1e88e5' },
              }}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
        </Box>
      )}
      {/* History FAB */}
      {presentationDetails && (
        <Box sx={{ position: 'fixed', bottom: 16, left: canEdit ? 72 : 16, zIndex: 2000 }}>
          <Tooltip title="Presentation history">
            <IconButton
              size="large"
              aria-label="Open presentation history"
              onClick={() => setHistoryOpen(true)}
              sx={{
                bgcolor: '#424242',
                color: '#fff',
                border: '1px solid #2e2e2e',
                boxShadow: 2,
                '&:hover': { boxShadow: 4, bgcolor: '#515151' },
              }}
            >
              <HistoryIcon />
            </IconButton>
          </Tooltip>
        </Box>
      )}
      {/* History Drawer (saved revisions of this presentation) */}
      <Drawer
        anchor="right"
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        PaperProps={{ sx: { width: { xs: '100%', sm: 420 } } }}
      >
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">Presentation history</Typography>
          <IconButton onClick={() => setHistoryOpen(false)} size="large">
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider />

        <Box sx={{ p: 2 }}>
          {loadingHistory ? (
            <LoadingComponent />
          ) : historyError ? (
            <Alert severity="error">Unable to load presentation revision history.</Alert>
          ) : historyList.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No saved revisions found for this presentation.</Typography>
          ) : (
            <List dense>
              {historyList.map((revision) => {
                const currentRevision = Number(presentationDetails?.snapshotMeta?.revisionNumber || 1)
                const isCurrent = Number(revision.revisionNumber) === currentRevision
                const date = revision.createdAt ? new Date(revision.createdAt) : null
                return (
                  <React.Fragment key={revision.id || revision.snapshotId || revision.revisionNumber}>
                    <ListItemText
                      primary={`Revision #${revision.revisionNumber || '—'}${isCurrent ? ' (current)' : ''}`}
                      secondary={
                        `${date && !Number.isNaN(date.getTime()) ? date.toLocaleString() : 'Date unavailable'} · ${revision.createdByName || 'System'} · ${revision.itemCount ?? 0} items`
                      }
                    />
                    <Divider />
                  </React.Fragment>
                )
              })}
            </List>
          )}
        </Box>
      </Drawer>
    </>
  );
}

export default Presentations
